"use client";

import { useBreakpointValue } from "@chakra-ui/react";
import {
    Children,
    isValidElement,
    type ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Button } from "../../forms/button/Button";
import { Box } from "../../layout/box/Box";
import { H } from "../heading/A11yHeading";
import { styles } from "./PageHeading.styles";
import { mapSizesDescription, mapSizesTitle } from "./mapSizes";

export interface PageHeadingProps {
    title: string;
    description: string | ReactNode;
    showMoreLabel: string;
    showLessLabel: string;
    titleSize?: string;
    descriptionSize?: string;
    resultsCount?: number;
}

export const PageHeading = ({
    title,
    description,
    showMoreLabel,
    showLessLabel,
    titleSize = "Extra Large",
    descriptionSize = "Normal",
    resultsCount,
}: PageHeadingProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const hasShowMoreLessLabel = Boolean(showMoreLabel && showLessLabel);
    const [isExpanded, setIsExpanded] = useState(!hasShowMoreLessLabel);
    const [showButton, setShowButton] = useState<boolean>(false);

    const handleToggle = () => setIsExpanded((prevState) => !prevState);

    const maxLines = useBreakpointValue({ base: 2, lg: 3 });

    const showDescription = useMemo(() => {
        if (typeof description === "string") {
            return description.length > 0;
        }
        if (isValidElement(description)) {
            if ("data" in description.props) {
                return Boolean(description.props.data);
            }
            return Children.count(description.props.children) > 0;
        }
        return Boolean(description);
    }, [description]);

    useEffect(() => {
        if (!ref.current || isExpanded) return;

        const isOverflowing = ref.current.scrollHeight > ref.current.clientHeight;
        setShowButton(isOverflowing);
    }, [maxLines, description, isExpanded]);

    return (
        <Box sx={styles.container}>
            {title && (
                <H
                    as="h1"
                    size="h1"
                    fontWeight="bold"
                    fontSize={mapSizesTitle[titleSize]}
                    resultsCount={resultsCount}
                >
                    {title}
                </H>
            )}
            {showDescription && (
                <Box sx={styles.descriptionContainer}>
                    <Box
                        ref={ref}
                        sx={{
                            ...styles.description,

                            WebkitLineClamp: isExpanded ? "none" : maxLines,
                            "& p": {
                                mb: isExpanded && 0,
                                fontWeight: "bold",
                                fontSize: mapSizesDescription[descriptionSize],
                            },
                        }}
                    >
                        {description}
                    </Box>
                    {showButton && hasShowMoreLessLabel && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                            }}
                        >
                            <Button onClick={handleToggle} variant="link">
                                {isExpanded ? showLessLabel : showMoreLabel}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};
